/* ============================================================================
   CRÉATION — la sesión de diseño del cliente, que sobrevive a la pestaña
   ----------------------------------------------------------------------------
   Hasta ahora el diseño vivía en `sessionStorage`. Eso significa que cerrar la
   pestaña BORRABA el trabajo: media hora colocando una foto y un texto,
   perdida sin aviso y sin nada que recuperar. Es la peor pérdida que tenía el
   Studio y no se veía nunca en pruebas, porque probando no se cierra la
   pestaña.

   POR QUÉ INDEXEDDB Y NO localStorage: el diseño lleva las miniaturas de las
   fotos en base64 dentro del JSON del lienzo. Una foto de móvil reducida son
   cientos de kilobytes y `localStorage` da ~5 MB para TODO el dominio; el
   código actual ya avisaba de « sessionStorage plein ». IndexedDB no tiene ese
   techo y guarda objetos sin serializar a mano.

   Pero el índice —qué creaciones hay, de qué producto y cuándo— sí va en
   `localStorage`: es diminuto y así la tarjeta « Votre création vous attend »
   se puede pintar sin abrir la base de datos ni esperar a nada.

   LO QUE NO HACE, a propósito:
   · no manda nada a ningún servidor. Sin backend no hay backend que inventar.
   · no guarda la foto ORIGINAL a pantalla completa: eso pesa megas y ya tiene
     su camino —Cloudinary— cuando el diseño va al carrito. Al volver, el
     cliente recupera su composición; si quiere el original a 300 DPI vuelve a
     subir la foto, y el editor lo dice en vez de fingirlo.
   ========================================================================== */

const CREATION = (() => {
'use strict';

const BD = 'edisamp-creations';
const ALMACEN = 'sesiones';
const CLAVE_INDICE = 'edisamp.creations.index';
const VERSION_ESQUEMA = 1;
const MAX_INDICE = 8;          // pocas recientes, no un panel de gestión
const DEBOUNCE = 800;

/* ------------------------------------------------------------ IndexedDB -- */

let _bd = null;
function abrir() {
  if (_bd) return _bd;
  _bd = new Promise((ok, mal) => {
    let pet;
    try { pet = indexedDB.open(BD, 1); } catch (e) { return mal(e); }
    pet.onupgradeneeded = () => {
      const db = pet.result;
      if (!db.objectStoreNames.contains(ALMACEN)) {
        db.createObjectStore(ALMACEN, { keyPath: 'designId' });
      }
    };
    pet.onsuccess = () => ok(pet.result);
    pet.onerror = () => mal(pet.error);
  }).catch((e) => { _bd = null; throw e; });
  return _bd;
}

function conAlmacen(modo, fn) {
  return abrir().then((db) => new Promise((ok, mal) => {
    const tx = db.transaction(ALMACEN, modo);
    const pet = fn(tx.objectStore(ALMACEN));
    tx.oncomplete = () => ok(pet && pet.result);
    tx.onerror = () => mal(tx.error);
    tx.onabort = () => mal(tx.error);
  }));
}

/* --------------------------------------------------------------- índice -- */

function indice() {
  try { return JSON.parse(localStorage.getItem(CLAVE_INDICE) || '[]'); }
  catch (e) { return []; }
}

function escribirIndice(lista) {
  try {
    localStorage.setItem(CLAVE_INDICE, JSON.stringify(lista.slice(0, MAX_INDICE)));
  } catch (e) { /* modo privado: se pierde el índice, no el diseño */ }
}

function anotar(meta) {
  const lista = indice().filter((x) => x.designId !== meta.designId);
  lista.unshift(meta);
  lista.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  escribirIndice(lista);
}

/* ---------------------------------------------------------------- estado -- */

let _id = null;
let _t = null;
let _pendiente = null;
const _oyentes = [];

const avisar = (estado, detalle) => _oyentes.forEach((f) => {
  try { f(estado, detalle); } catch (e) { /* un oyente roto no para el guardado */ }
});

/** Un identificador que no depende de la hora del reloj para ser único. */
function nuevoId() {
  const azar = (crypto && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `d-${Date.now().toString(36)}-${azar}`;
}

/* --------------------------------------------------------------- público -- */

/**
 * Empieza o retoma una sesión para un producto.
 * @param {string} designId  para retomar una concreta; si no, se crea una nueva
 */
function comenzar(designId) {
  _id = designId || nuevoId();
  return _id;
}

const idActual = () => _id;

/**
 * Guarda. Rebotado: arrastrar un elemento genera decenas de cambios y produce
 * UNA escritura al soltar, no cuarenta.
 *
 * @param {object} datos  lo que el Studio considera su sesión:
 *   { productHandle, productTitle, variant, color, vue, zones, designs, fond,
 *     preview }
 */
function guardar(datos) {
  if (!_id) comenzar();
  _pendiente = datos;
  clearTimeout(_t);
  avisar('saving');
  /* El id se captura AHORA, no cuando venza el rebote. Si entre medias se
     crea una variante, `_id` ya apunta a la nueva y este guardado —que es de
     la anterior— aterrizaría encima de ella. Una versión pisando a otra es
     justo lo que las versiones vienen a impedir. */
  const destino = _id;
  _t = setTimeout(() => { escribir(_pendiente, destino).catch(() => {}); }, DEBOUNCE);
}

/** Escribe ya, sin esperar al rebote. Para cuando la página se va a cerrar. */
function guardarAhora(datos) {
  clearTimeout(_t);
  return escribir(datos || _pendiente, _id);
}

async function escribir(datos, destino) {
  const id = destino || _id;
  if (!datos || !id) return null;
  const registro = {
    designId: id,
    schema: VERSION_ESQUEMA,
    productHandle: datos.productHandle ?? null,
    productTitle: datos.productTitle ?? null,
    variant: datos.variant ?? null,
    color: datos.color ?? null,
    vue: datos.vue ?? null,
    zones: datos.zones ?? {},
    designs: datos.designs ?? {},
    fond: datos.fond ?? {},
    preview: datos.preview ?? null,
    createdAt: datos.createdAt ?? Date.now(),
    updatedAt: Date.now(),
  };

  try {
    await conAlmacen('readwrite', (s) => s.put(registro));
    // El índice va DESPUÉS de que el diseño esté a salvo: al revés, una
    // tarjeta « continuer » podría apuntar a algo que no se llegó a escribir.
    anotar({
      designId: registro.designId,
      productHandle: registro.productHandle,
      productTitle: registro.productTitle,
      preview: registro.preview,
      updatedAt: registro.updatedAt,
    });
    avisar('saved', registro.updatedAt);
    return registro;
  } catch (e) {
    // Nunca en silencio: perder el trabajo sin decirlo es lo que se venía a
    // arreglar.
    avisar('error', e && e.message);
    return null;
  }
}

async function cargar(designId) {
  try { return (await conAlmacen('readonly', (s) => s.get(designId))) || null; }
  catch (e) { return null; }
}

/** Las pocas recientes, ya ordenadas. Sale del índice: no abre la base. */
const recientes = (n = 3) => indice().slice(0, n);

/** La más reciente de un producto, o la más reciente a secas. */
function ultima(productHandle) {
  const l = indice();
  return (productHandle ? l.find((x) => x.productHandle === productHandle) : l[0]) || null;
}

/**
 * Una variante: misma creación de partida, vida independiente.
 *
 * El caso real es « lo tengo grande delante… ¿y pequeño en el pecho? ». Sin
 * esto, probarlo obliga a destruir lo primero. Con esto, lo primero se queda
 * donde está.
 *
 * Cada variante es un DesignSession propio, con su designId. No es una rama
 * ni un árbol: es otra creación que empieza donde acababa la anterior, y
 * `parentDesignId` sólo deja constancia de dónde vino.
 */
async function duplicar(designId, { etiqueta = null } = {}) {
  const origen = await cargar(designId || _id);
  if (!origen) return null;

  const copia = {
    ...origen,
    designId: nuevoId(),
    parentDesignId: origen.designId,
    etiqueta,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  try {
    await conAlmacen('readwrite', (s) => s.put(copia));
  } catch (e) { avisar('error', e && e.message); return null; }

  anotar({
    designId: copia.designId,
    productHandle: copia.productHandle,
    productTitle: copia.productTitle,
    preview: copia.preview,
    parentDesignId: copia.parentDesignId,
    updatedAt: copia.updatedAt,
  });

  /* El guardado pendiente de la sesión ANTERIOR se cancela: ya no tiene a
     dónde ir sin pisar algo. Lo que hubiera sin escribir se acaba de copiar
     en la variante de todos modos. */
  clearTimeout(_t);
  _id = copia.designId;
  return copia;
}

/** Las versiones de un mismo producto, la más reciente primero. */
function versiones(productHandle) {
  return indice()
    .filter((x) => !productHandle || x.productHandle === productHandle)
    .map((x, i) => ({ ...x, numero: i + 1 }));
}

async function borrar(designId) {
  escribirIndice(indice().filter((x) => x.designId !== designId));
  try { await conAlmacen('readwrite', (s) => s.delete(designId)); } catch (e) {}
}

const alGuardar = (f) => { _oyentes.push(f); return () => {
  const i = _oyentes.indexOf(f); if (i >= 0) _oyentes.splice(i, 1);
}; };

/** ¿Se puede guardar de verdad aquí? Modo privado y navegadores viejos. */
const disponible = () => {
  try { return typeof indexedDB !== 'undefined' && !!indexedDB; }
  catch (e) { return false; }
};

return {
  comenzar, idActual, guardar, guardarAhora, cargar, recientes, ultima,
  duplicar, versiones, borrar, alGuardar, disponible, VERSION_ESQUEMA,
};
})();

if (typeof window !== 'undefined') window.CREATION = CREATION;
